import { z } from 'zod'
import { UuidSchema } from '../../../utils/zodUuid'
import { an } from '../../../utils/an'
import {
  createLongTextValueSchema as createLongTextCreationValueSchema,
  createSelectionValueSchema as createPayItemsCreationValueSchema,
} from './fieldValueSchemas'
import type {
  ApprovalCreateFieldOptions,
  ApprovalCreationInitialValueTransformer,
  ApprovalOptionCreationValue,
  CreateFieldOptions,
  CreationValue,
  CreationValues,
  CreationTargetStatus,
  CreateWorkflowRecordValues,
  GeneratedIntCreationValue,
  LongTextCreationInitialValueTransformer,
  LongTextCreationValue,
  PayItemsCreationInitialValueTransformer,
  PayItemsCreationValue,
  TargetStatusInput,
} from './creation'
import {
  ConfiguredFieldValuesInputSchema,
  createConfiguredOptionValueSchema,
} from './configuredFieldSchemas'

export { createLongTextCreationValueSchema, createPayItemsCreationValueSchema }

const DraftCreationTargetStatusSchema = z
  .object({
    type: z.literal('draft'),
    statusId: z.never().optional(),
  })
  .strict()

const StatusCreationTargetStatusSchema = z
  .object({
    type: z.literal('status'),
    statusId: UuidSchema,
  })
  .strict()

/** The normalized target-status intent used after parsing a GraphQL input. */
export const CreationTargetStatusSchema = z.discriminatedUnion('type', [
  DraftCreationTargetStatusSchema,
  StatusCreationTargetStatusSchema,
]) satisfies z.ZodType<CreationTargetStatus>

/**
 * Parses the nullable GraphQL input shape into the stricter internal target-status
 * union.
 */
export const TargetStatusInputSchema = z
  .object({
    type: z.string(),
    statusId: z.string().nullable().optional(),
  })
  .strict()
  .pipe(CreationTargetStatusSchema) satisfies z.ZodType<
  CreationTargetStatus,
  TargetStatusInput
>

/** The untrusted field-ID-keyed JSON object accepted by record creation. */
export const CreateWorkflowRecordValuesSchema =
  ConfiguredFieldValuesInputSchema satisfies z.ZodType<CreateWorkflowRecordValues>

/** The pending Approval Groups option shape accepted during record creation. */
export const ApprovalOptionCreationValueSchema = z
  .object({
    optionId: UuidSchema,
    state: z.null(),
  })
  .strict() satisfies z.ZodType<ApprovalOptionCreationValue>

function creationApprovalIssueMessage(message: string): string {
  if (message === 'Options must not contain duplicate IDs')
    return 'Approval options must not contain duplicate IDs'
  if (message === 'Options must be active or retained current options')
    return 'Approval options must be active options for this field'
  return message
}

/**
 * Creates an Approval Groups schema from the currently active option IDs and
 * effective creation rules. Submitted IDs outside that active set are rejected.
 */
export function createApprovalGroupsCreationValueSchema(
  activeOptionIds: readonly string[],
  required: boolean | 'all' = 'all',
  single = false
) {
  const selectionIntentSchema = createConfiguredOptionValueSchema({
    activeOptionIds,
    required,
    single,
  })

  return z
    .array(ApprovalOptionCreationValueSchema)
    .default([])
    .superRefine((value, context) => {
      const result = selectionIntentSchema.safeParse(
        value.map(({ optionId }) => optionId)
      )
      if (result.success) return

      for (const issue of result.error.issues)
        context.addIssue({
          ...issue,
          message: creationApprovalIssueMessage(issue.message),
        })
    }) satisfies z.ZodType<ApprovalOptionCreationValue[]>
}

/**
 * Builds a new record's pending approval selection by applying enabled
 * `overwrite` transformers in order.
 *
 * `clear_option_state` is intentionally inert because creation has no prior
 * approval decisions to clear; every selected option starts with a `null` state.
 */
export function createApprovalGroupsInitialValue(
  options: readonly Pick<ApprovalCreateFieldOptions['options'][number], 'id'>[],
  transformers: readonly ApprovalCreationInitialValueTransformer[]
): ApprovalOptionCreationValue[] {
  const allOptionIds = options.map(({ id }) => id)
  let optionIds: readonly string[] = []

  for (const transformer of transformers) {
    if (!transformer.enabled || transformer.type !== 'overwrite') continue
    optionIds = transformer.value === 'all' ? allOptionIds : transformer.value
  }

  return optionIds.map((optionId) => ({ optionId, state: null }))
}

/** Applies enabled long-text overwrites in their configured order. */
export function createLongTextInitialValue(
  transformers: readonly LongTextCreationInitialValueTransformer[]
): LongTextCreationValue {
  let value: LongTextCreationValue = null
  for (const transformer of transformers) {
    if (transformer.enabled) value = transformer.value
  }
  return value
}

/** Applies enabled pay-item overwrites using the currently active option IDs. */
export function createPayItemsInitialValue(
  activeOptionIds: readonly string[],
  transformers: readonly PayItemsCreationInitialValueTransformer[]
): PayItemsCreationValue {
  let value: readonly string[] = []
  for (const transformer of transformers) {
    if (!transformer.enabled) continue
    value = transformer.value === 'all' ? activeOptionIds : transformer.value
  }
  return [...value]
}

/** Resolves one creation field's client-side initial value. */
export function createCreationFieldInitialValue(
  field: CreateFieldOptions,
  activePayItemIds: readonly string[] = []
): CreationValue {
  if (field.type === 'long_text')
    return createLongTextInitialValue(field.initialValueTransformers)
  else if (field.type === 'pay_items')
    return createPayItemsInitialValue(
      activePayItemIds,
      field.initialValueTransformers
    )
  else
    return createApprovalGroupsInitialValue(
      field.options,
      field.initialValueTransformers
    )
}

/** The persisted generated-integer field value shape. */
export const GeneratedIntCreationValueSchema = z
  .object({
    type: z.literal('generated_int'),
    value: z.int(),
  })
  .strict() satisfies z.ZodType<GeneratedIntCreationValue>

function creationFieldValueSchema(field: CreateFieldOptions) {
  if (field.type === 'long_text')
    return createLongTextCreationValueSchema(field.required)
  else if (field.type === 'pay_items')
    return createPayItemsCreationValueSchema(field.required, field.single)
  else
    return createApprovalGroupsCreationValueSchema(
      field.options.map(({ id }) => id),
      field.required,
      field.single
    )
}

function requiredCreationFieldMessage(field: CreateFieldOptions): string {
  if (field.type === 'long_text')
    return `Please enter ${an(field.name.toLowerCase())}`
  else if (field.type === 'pay_items')
    return 'Please select at least one pay item'
  else if (field.required === 'all')
    return 'Please select every approval option'
  else return 'Please select at least one approval option'
}

/**
 * Builds the shared client/server boundary for submitted creation values.
 *
 * Missing input keys receive their canonical empty values, after which required
 * rules are applied. Approval values are expected after the client applies their
 * initial-value settings. Unknown ordinary keys are stripped, while known
 * generated-integer keys are rejected because the server must allocate them.
 */
export function createCreationValuesSchema(
  fields: readonly CreateFieldOptions[],
  generatedIntFieldIds: readonly string[] = []
) {
  return CreateWorkflowRecordValuesSchema.transform((values, context) => {
    const normalized: CreationValues = {}

    for (const fieldId of generatedIntFieldIds) {
      if (Object.prototype.hasOwnProperty.call(values, fieldId))
        context.addIssue({
          code: 'custom',
          path: [fieldId],
          message: 'Generated values cannot be submitted',
        })
    }

    for (const field of fields) {
      const result = creationFieldValueSchema(field).safeParse(values[field.id])
      if (result.success) normalized[field.id] = result.data
      else
        for (const issue of result.error.issues)
          context.addIssue({
            ...issue,
            path: [field.id, ...issue.path],
            message:
              issue.message === 'Required'
                ? requiredCreationFieldMessage(field)
                : issue.message,
          })
    }

    return normalized
  }) satisfies z.ZodType<CreationValues, unknown>
}
